import { InternalAxiosRequestConfig, AxiosResponse, AxiosError } from "axios";
import axios from "axios";
import { showError } from '../toast/toastUtil';
import config from '../../config';
import { tokenExpiryManager } from '../tokenExpiryManager';
import { hasTokens } from '../auth/tokenValidator';
import { 
  handle401Error, 
  handleNetworkError, 
  handleTokenValidationError,
  showSessionErrorModal,
  redirectToSignin
} from '../auth/sessionErrorHandler';

// 전역 세션 만료 모달 핸들러
let showSessionExpiredModal: ((message?: string) => void) | null = null;

export const setSessionExpiredModalHandler = (handler: (message?: string) => void) => {
  showSessionExpiredModal = handler;
};

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// ============================================
// 1️⃣ 요청 인터셉터
// ============================================
export const onRequest = (config: InternalAxiosRequestConfig) => {
  // 인증이 필요 없는 엔드포인트
  const noAuthEndpoints = [
    '/auth/signin',
    '/auth/refresh',
    '/auth/signup',
    '/auth/find',
    'business.juso.go.kr'
  ];
  
  const needsAuth = !noAuthEndpoints.some(endpoint => 
    config.url?.includes(endpoint)
  );
  
  if (needsAuth) {
    const accessToken = localStorage.getItem('access-token');
    if (accessToken !== null) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
  }

  return config;
};

export const onErrorRequest = (error: Error) => {
  return Promise.reject(error);
};


// ============================================
// 2️⃣ 응답 인터셉터 (AT+RT 자동 갱신 처리)
// ============================================
export const onResponse = (response: AxiosResponse) => {
  const newAccessToken = response.headers['new-access-token'] || response.headers['New-Access-Token'] || response.headers['NEW-ACCESS-TOKEN'];
  const newRefreshToken = response.headers['new-refresh-token'] || response.headers['New-Refresh-Token'] || response.headers['NEW-REFRESH-TOKEN'];

  if (newAccessToken) {
    localStorage.setItem('access-token', newAccessToken);
    if (newRefreshToken) {
      localStorage.setItem('refresh-token', newRefreshToken);
    }
    // AT+RT 둘 다 전달해서 타이머 재설정
    tokenExpiryManager.setTokens(
      newAccessToken,
      newRefreshToken ?? localStorage.getItem('refresh-token')
    );
  }
  return response;
};

// ============================================
// 3️⃣ 에러 인터셉터 (수동 갱신 처리)
// ============================================
export const onErrorResponse = async (error: AxiosError<{
  code: string;
  message: string;
  detailMessage: string;
}>) => {
  try {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const isDev = config.appMode === 'dev' || config.runMode === 'dev';

    //  dev 환경에서는 모든 에러를 먼저 확인
    if (isDev) {
      const status = error.response?.status || 'UNDEFINED';
      const errorType = !error.response 
        ? 'NETWORK/CORS' 
        : !error.response.status 
        ? 'NO_STATUS' 
        : error.response.status;

      
      const message = error.response?.data?.detailMessage 
        || error.response?.data?.message 
        || error.message 
        || '알 수 없는 오류';
      showError(`[DEV] ${errorType} 에러: ${message}`);
      
      // dev 환경에서는 무조건 여기서 종료
      return Promise.resolve();
    }

    // ===== 이하 프로덕션 환경에서만 실행됨 =====
    
    // 401 에러 처리
    if (error.response?.status === 401) {
      //  dev 환경에서는 401 에러도 자세히 표시
      if (isDev) {
        showError('[DEV] 401 인증 에러 발생');
        return Promise.resolve();
      }

      // 이미 재시도했거나 로그인 API인 경우
      if (originalRequest._retry || originalRequest.url?.includes('/auth/signin')) {
        handle401Error('api-call', error, false);
        return Promise.resolve();
      }

      // refresh API 자체가 실패한 경우 (유휴 타임아웃)
      if (originalRequest.url?.includes('/auth/refresh')) {
        handle401Error('token-refresh', error, false);
        return Promise.resolve();
      }

      // 토큰 갱신 시도
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axios(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      // 토큰 존재 여부 체크 (공통 함수 사용)
      if (!hasTokens()) {
        handleTokenValidationError('api-call');
        return Promise.resolve();
      }

      const refreshToken = localStorage.getItem('refresh-token');

      try {
        // 🔄 RefreshToken으로 수동 갱신 시도 (직접 호출하여 순환 의존성 제거)
        const response = await axios.post(`${config.apiUrl}/auth/refresh`, { refreshToken });
        if (!response?.data?.successFlag) {
          throw new Error('토큰 갱신 실패');
        }

        const { accessToken, refreshToken: newRefreshToken } = response.data.data;

        localStorage.setItem('access-token', accessToken);
        localStorage.setItem('refresh-token', newRefreshToken);

        processQueue(null, accessToken);

        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        // 갱신 성공 시 만료 타이머 재설정
        tokenExpiryManager.setTokens(accessToken, newRefreshToken);
        return axios(originalRequest);

      } catch (refreshError: any) {
        processQueue(refreshError, null);
        handle401Error('token-refresh', refreshError, false);
        return Promise.resolve();
      } finally {
        isRefreshing = false;
      }
    }

    // 401이 아닌 다른 에러 처리
    if (error.response) {
      //  status가 없는 경우 처리
      if (!error.response.status) {
        console.error('🔴 응답은 있지만 status가 없는 에러:', error);
        showError('서버 응답 오류가 발생했습니다. (status 없음)');
        return Promise.reject(error);
      }

      const responseData = error.response.data || {};
      const message = responseData.detailMessage 
        ? `${responseData.detailMessage} : ${responseData.message || '오류가 발생했습니다.'}`
        : responseData.message || '오류가 발생했습니다.';
      
      // 500 에러인 경우 특정 경로에서만 세션 만료 모달 표시
      if (error.response.status >= 500) {
        const specificPaths = ['/auth/refresh'];
        const isSpecificPath = specificPaths.some(path => 
          originalRequest.url?.includes(path)
        );
        
        if (isSpecificPath) {
          showSessionErrorModal('서버 오류가 발생했습니다. 다시 로그인해주세요.');
          return Promise.resolve();
        } else {
          showError(message);
        }
      } else {
        showError(message);
      }
    } else {
      //  dev 환경에서는 네트워크 에러도 자세히 표시
      if (isDev) {
        console.group('🔴 [DEV] 네트워크 에러 발생');
        console.error('URL:', originalRequest?.url);
        console.error('메서드:', originalRequest?.method);
        console.error('요청 데이터:', originalRequest?.data);
        console.error('에러:', error);
        console.trace('호출 스택:');
        console.groupEnd();
        showError('[DEV] 네트워크 에러 발생');
        return Promise.resolve(); //  dev에서는 리다이렉트 안함
      }
      
      // 네트워크 에러나 기타 에러
      handleNetworkError('network-error', error);
      return Promise.resolve();
    }
    
    return Promise.reject(error);
  } catch (interceptorError: any) {
    // dev 환경에서는 인터셉터 에러도 표시
    const isDev = config.appMode === 'dev' || config.runMode === 'dev';
    if (isDev) {
      showError('[DEV] 인터셉터 에러 발생');
      return Promise.resolve();
    }
    
    handleNetworkError('interceptor-error', interceptorError);
    return Promise.resolve();
  }
};

const axiosInterceptor = {
  onRequest,
  onErrorRequest,
  onResponse,
  onErrorResponse,
};

export default axiosInterceptor;
